import type { VacationBalance } from '../models/vacation.types';
import type { HistoryRequestRow, PeriodAllocation } from '../models/vacationHistoryReport.types';

const DISCARDED_STATUSES = ['Rejected'];
const TAKEN_STATUSES = ['Completed', 'OnVacation'];
const APPROVED_SCHEDULED_STATUSES = ['Scheduled'];
const PENDING_STATUSES = ['Draft', 'PendingManager', 'PendingClient', 'PendingDP', 'PendingCorrection'];

interface RowWithCategory {
    row: HistoryRequestRow;
    category: 'taken' | 'sold' | 'scheduled' | 'soldPending' | 'pending' | 'discarded';
    startDate: string;
}

export function allocateHistoryPeriods(
    rows: HistoryRequestRow[],
    balancesById: Map<string, VacationBalance>
): HistoryRequestRow[] {
    const byCollaborator = new Map<string, RowWithCategory[]>();

    rows.forEach((row) => {
        if (!balancesById.has(row.collaborator.id)) {
            return;
        }

        if (!row.requestStatus && !row.isManual) {
            return;
        }

        const status = row.requestStatus || (row.isManual && row.segments[0] ? 'Completed' : null);
        if (!status) return;

        let category: RowWithCategory['category'];
        if (DISCARDED_STATUSES.includes(status)) {
            category = 'discarded';
        } else if (TAKEN_STATUSES.includes(status)) {
            category = 'taken';
        } else if (APPROVED_SCHEDULED_STATUSES.includes(status)) {
            category = 'scheduled';
        } else if (PENDING_STATUSES.includes(status)) {
            category = 'pending';
        } else {
            return;
        }

        const startDate = row.segments[0]?.startDate || '';
        if (!startDate) return;

        if (!byCollaborator.has(row.collaborator.id)) {
            byCollaborator.set(row.collaborator.id, []);
        }

        byCollaborator.get(row.collaborator.id)!.push({
            row,
            category,
            startDate,
        });
    });

    const result: HistoryRequestRow[] = [];
    const processed = new Set<HistoryRequestRow>();

    byCollaborator.forEach((categoryRows, collaboratorId) => {
        const balance = balancesById.get(collaboratorId);
        if (!balance || !balance.periods || balance.periods.length === 0) {
            categoryRows.forEach(({ row }) => {
                result.push(row);
            });
            return;
        }

        const nonAccruingPeriods = balance.periods.filter((p) => p.status !== 'Accruing');
        const accruing = balance.periods.find((p) => p.status === 'Accruing');

        const categoryOrder: Array<RowWithCategory['category']> = ['taken', 'scheduled', 'pending', 'discarded'];
        let remainingCapacity = nonAccruingPeriods.map((p) => p.balanceDays || 30);

        categoryOrder.forEach((category) => {
            const categoryItems = categoryRows
                .filter((x) => x.category === category)
                .sort((a, b) => a.startDate.localeCompare(b.startDate));

            categoryItems.forEach(({ row }) => {
                if (category === 'discarded') {
                    const period = nonAccruingPeriods.find((p) => p.id === row.acquisitionPeriodId);
                    if (period) {
                        result.push({
                            ...row,
                            periodAllocations: [
                                {
                                    periodId: period.id,
                                    startDate: period.startDate,
                                    endDate: period.endDate,
                                    concessiveDeadline: period.concessiveDeadline,
                                    days: row.totalDays,
                                    isAccruing: false,
                                },
                            ],
                        });
                    } else {
                        result.push(row);
                    }
                    processed.add(row);
                    return;
                }

                const allocations: PeriodAllocation[] = [];
                let daysLeft = row.totalDays;

                for (let i = 0; i < nonAccruingPeriods.length && daysLeft > 0; i++) {
                    if (remainingCapacity[i]! <= 0) continue;

                    const daysInPeriod = Math.min(daysLeft, remainingCapacity[i]!);
                    allocations.push({
                        periodId: nonAccruingPeriods[i]!.id,
                        startDate: nonAccruingPeriods[i]!.startDate,
                        endDate: nonAccruingPeriods[i]!.endDate,
                        concessiveDeadline: nonAccruingPeriods[i]!.concessiveDeadline,
                        days: daysInPeriod,
                        isAccruing: false,
                    });

                    remainingCapacity[i]! -= daysInPeriod;
                    daysLeft -= daysInPeriod;
                }

                if (daysLeft > 0 && accruing) {
                    allocations.push({
                        periodId: accruing.id,
                        startDate: accruing.startDate,
                        endDate: accruing.endDate,
                        concessiveDeadline: accruing.concessiveDeadline,
                        days: daysLeft,
                        isAccruing: true,
                    });
                }

                result.push({
                    ...row,
                    periodAllocations: allocations.length > 0 ? allocations : undefined,
                });
                processed.add(row);
            });
        });

        categoryRows.forEach(({ row }) => {
            if (!processed.has(row)) {
                result.push(row);
            }
        });
    });

    rows.forEach((row) => {
        if (!result.includes(row) && !processed.has(row)) {
            result.push(row);
        }
    });

    return result;
}

export function formatPeriodLabel(startDate: string, endDate: string): string {
    const startYear = startDate.split('-')[0];
    const endYear = endDate.split('-')[0];
    return startYear === endYear ? startYear : `${startYear}–${endYear}`;
}
